import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output, signal
} from '@angular/core';

export interface GravacaoAudio {
  blob: Blob;
  duracaoSegundos: number;
}

type EstadoGravacao = 'ocioso' | 'gravando' | 'gravado';

/**
 * Gravador de áudio (MediaRecorder) para o painel do psicólogo. Funciona no Chrome
 * desktop e mobile. Emite o Blob + duração via {@link gravado} (emite `null` ao descartar).
 */
@Component({
  selector: 'app-audio-recorder',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule],
  template: `
    <div class="rec">
      @if (erro()) { <p class="rec__erro">{{ erro() }}</p> }

      @switch (estado()) {
        @case ('ocioso') {
          <button type="button" class="rec__btn rec__btn--rec" (click)="iniciar()">
            <i class="material-icons">mic</i> {{ rotuloGravar }}
          </button>
        }
        @case ('gravando') {
          <div class="rec__gravando">
            <span class="rec__pulse" aria-hidden="true"></span>
            <span class="rec__timer">{{ tempoFormatado() }}</span>
            <button type="button" class="rec__btn rec__btn--stop" (click)="parar()">
              <i class="material-icons">stop</i> Parar
            </button>
          </div>
        }
        @case ('gravado') {
          <div class="rec__preview">
            <audio [src]="previewUrl()!" controls preload="metadata"></audio>
            <button type="button" class="rec__btn rec__btn--ghost" (click)="descartar()">
              <i class="material-icons">refresh</i> Regravar
            </button>
          </div>
        }
      }
    </div>
  `,
  styles: [`
    :host { display: block; }
    .rec__erro { margin: 0 0 .5rem; font-size: .82rem; color: #b91c1c; }
    .rec__gravando, .rec__preview { display: flex; align-items: center; gap: .75rem; flex-wrap: wrap; }
    .rec__timer { font-variant-numeric: tabular-nums; font-weight: 700; min-width: 3ch; color: #ef4444; }
    .rec__pulse { width: 12px; height: 12px; border-radius: 50%; background: #ef4444; animation: rec-pulse 1s ease-in-out infinite; }
    .rec__preview audio { height: 40px; max-width: 100%; }
    .rec__btn {
      display: inline-flex; align-items: center; gap: .4rem;
      border: none; border-radius: 999px; cursor: pointer;
      font: inherit; font-weight: 600; font-size: .85rem; line-height: 1;
      padding: .5rem .9rem; transition: filter .15s ease, background .15s ease;
    }
    .rec__btn .material-icons { font-size: 1.15rem; }
    .rec__btn:hover { filter: brightness(.96); }
    .rec__btn--rec { background: rgba(99,102,241,.12); color: #4f46e5; }
    .rec__btn--stop { background: #ef4444; color: #fff; }
    .rec__btn--ghost { background: rgba(15,23,42,.06); color: #334155; }
    @keyframes rec-pulse { 0%,100% { opacity: 1; } 50% { opacity: .3; } }
  `]
})
export class AudioRecorderComponent implements OnDestroy {
  @Input() rotuloGravar = 'Gravar áudio';
  @Input() duracaoMaximaSegundos = 300;
  @Output() gravado = new EventEmitter<GravacaoAudio | null>();

  readonly estado = signal<EstadoGravacao>('ocioso');
  readonly erro = signal<string | null>(null);
  readonly segundos = signal(0);
  readonly previewUrl = signal<string | null>(null);

  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private stream: MediaStream | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;

  tempoFormatado(): string {
    const s = this.segundos();
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  }

  async iniciar(): Promise<void> {
    this.erro.set(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this.erro.set('Este navegador não permite gravação de áudio.');
      return;
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      this.erro.set('Não foi possível acessar o microfone. Verifique a permissão do navegador.');
      return;
    }

    this.chunks = [];
    this.mediaRecorder = new MediaRecorder(this.stream, this.opcoesGravacao());
    this.mediaRecorder.ondataavailable = e => { if (e.data.size > 0) this.chunks.push(e.data); };
    this.mediaRecorder.onstop = () => this.finalizar();
    this.mediaRecorder.start();

    this.segundos.set(0);
    this.estado.set('gravando');
    this.timer = setInterval(() => {
      this.segundos.update(v => v + 1);
      if (this.segundos() >= this.duracaoMaximaSegundos) this.parar();
    }, 1000);
  }

  parar(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    this.limparTimer();
  }

  descartar(): void {
    this.revogarPreview();
    this.estado.set('ocioso');
    this.segundos.set(0);
    this.gravado.emit(null);
  }

  private finalizar(): void {
    this.pararStream();
    const tipo = this.mediaRecorder?.mimeType || 'audio/webm';
    const blob = new Blob(this.chunks, { type: tipo });
    this.revogarPreview();
    this.previewUrl.set(URL.createObjectURL(blob));
    this.estado.set('gravado');
    this.gravado.emit({ blob, duracaoSegundos: this.segundos() });
  }

  private opcoesGravacao(): MediaRecorderOptions {
    const candidatos = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
    const suportado = candidatos.find(t => MediaRecorder.isTypeSupported?.(t));
    return suportado ? { mimeType: suportado } : {};
  }

  private limparTimer(): void {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  private pararStream(): void {
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = null;
  }

  private revogarPreview(): void {
    const url = this.previewUrl();
    if (url) { URL.revokeObjectURL(url); this.previewUrl.set(null); }
  }

  ngOnDestroy(): void {
    this.limparTimer();
    this.pararStream();
    this.revogarPreview();
  }
}
